import { useState } from "react";
import { Bell, Check, X, Youtube, AlertCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { safeDate } from "@/lib/safeDate";
import type { Notification } from "@shared/schema";
import { useUnreadNotificationCount } from "@/hooks/useUnreadNotificationCount";

function getNotificationIcon(type: string) {
  switch (type) {
    case "sync_complete":
    case "import_complete":
      return <Youtube className="h-4 w-4 text-red-500" />;
    case "error":
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    default:
      return <Info className="h-4 w-4 text-muted-foreground" />;
  }
}

function NotificationsPanel({ enabled }: { enabled: boolean }) {
  const unreadCount = useUnreadNotificationCount();

  const { data: notifications = [], isLoading } = useQuery<Notification[]>({
    queryKey: ["/api/notifications"],
    queryFn: async () => [],
    enabled,
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("PATCH", `/api/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("PATCH", "/api/notifications/read-all");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/notifications/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const visibleNotifications = notifications.filter(n => !n.dismissedAt);

  return (
    <>
      <DropdownMenuLabel className="flex items-center justify-between">
        <span>Notifications</span>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto py-1 px-2 text-xs"
            onClick={(e) => {
              e.preventDefault();
              markAllReadMutation.mutate();
            }}
            data-testid="button-mark-all-read"
          >
            Mark all read
          </Button>
        )}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <ScrollArea className="h-[300px]">
        {isLoading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : visibleNotifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground" data-testid="text-no-notifications">
            No notifications
          </div>
        ) : (
          visibleNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-3 border-b last:border-b-0 ${
                !notification.isRead ? "bg-muted/50" : ""
              }`}
              data-testid={`notification-item-${notification.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate" data-testid={`text-notification-title-${notification.id}`}>
                      {notification.title}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      {!notification.isRead && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.preventDefault();
                            markReadMutation.mutate(notification.id);
                          }}
                          data-testid={`button-mark-read-${notification.id}`}
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.preventDefault();
                          dismissMutation.mutate(notification.id);
                        }}
                        data-testid={`button-dismiss-${notification.id}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1" data-testid={`text-notification-message-${notification.id}`}>
                    {notification.message}
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    {(() => {
                      const created = safeDate(notification.createdAt);
                      return created ? formatDistanceToNow(created, { addSuffix: true }) : "";
                    })()}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </ScrollArea>
    </>
  );
}

export function NotificationsMenuSub() {
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = useUnreadNotificationCount();

  return (
    <DropdownMenuSub open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuSubTrigger data-testid="menu-notifications">
        <Bell className="h-4 w-4 mr-2" />
        <span>Notifications</span>
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="ml-auto h-5 min-w-5 px-1 flex items-center justify-center text-xs"
            data-testid="badge-menu-unread-count"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </Badge>
        )}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-80" data-testid="dropdown-notifications">
        <NotificationsPanel enabled={isOpen} />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = useUnreadNotificationCount();

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="button-notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
              data-testid="badge-unread-count"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="dropdown-notifications">
        <NotificationsPanel enabled={isOpen} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
